import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaArrowCircleLeft, FaArrowCircleRight } from 'react-icons/fa'
import {
  MdAutoFixHigh,
  MdCheckCircle,
  MdChecklist,
  MdDescription,
  MdFactCheck,
  MdMarkEmailRead,
  MdSend,
  MdVerified
} from 'react-icons/md'
import { fetchData } from '../../helpers/fetchData'
import { sendClaim, matchClaimInsurancePlan } from '../../services/InvoicesService'
import { IoIosSend } from 'react-icons/io'
import PageHeader from '../../shared/PageHeader'
import CustomButton from '../../shared/CustomButton'
import CustomChip from '../../shared/CustomChip'
import ReusableModal from '../../shared/ReuseableModal'
import ConfirmationModal from '../../shared/ConfirmationModal'
import { Skeleton } from 'primereact/skeleton'

const timelineSteps = [
  { key: 'create_batch', label: 'Create Batch', icon: MdChecklist },
  { key: 'audit', label: 'Audit', icon: MdFactCheck },
  { key: 'auto_fix', label: 'Auto Correct', icon: MdAutoFixHigh },
  { key: 'acknowledgment', label: 'Acknowledge', icon: MdMarkEmailRead },
  { key: 'generate_claim', label: 'Generate Claim', icon: MdDescription },
  { key: 'generated_claim_audit', label: 'Generated Claim Audit', icon: MdFactCheck },
  { key: 'generated_claim_auto_fix', label: 'Generated Claim Auto Correct', icon: MdAutoFixHigh },
  { key: 'clearance', label: 'Clearance', icon: MdVerified },
  { key: 'submit_claim', label: 'Submit Claim', icon: MdSend }
]

const formatAmount = value => Number(value || 0).toFixed(2)

const formatDateTime = value => {
  if (!value) return '-'
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('DD/MM/YYYY hh:mm A') : '-'
}

const formatDate = value => {
  if (!value) return '-'
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '-'
}

const normalizeText = value =>
  String(value || '')
    .replace(/_/g, ' ')
    .trim()

const formatFailureError = value => {
  const message = String(value || '').trim()
  return message || 'The job could not complete this record.'
}

const statusChipType = status => {
  switch ((status || '').toLowerCase()) {
    case 'completed':
    case 'resolved':
      return 'success'
    case 'completed_with_errors':
    case 'processing':
      return 'warning'
    case 'queued':
      return 'blue'
    case 'failed':
      return 'danger'
    case 'stopped':
    case 'pending':
      return 'secondary'
    default:
      return 'secondary'
  }
}

const getJobResultPercent = (job, result) => {
  const successCount = Number(job?.success_count || 0)
  const failedCount = Number(job?.failed_count || 0)
  const processedCount = Math.max(
    Number(job?.processed_count || 0),
    successCount + failedCount
  )

  if (!processedCount) return 0
  return ((result === 'success' ? successCount : failedCount) / processedCount) * 100
}

const getPayerDisplay = batch => {
  const fromDisplay = String(batch?.payers_display || '').trim()
  if (fromDisplay && fromDisplay !== '-') return fromDisplay

  const selected = (batch?.selected_payer_names || []).filter(Boolean)
  if (selected.length) return selected.join(', ')

  const payers = (batch?.payers || [])
    .map(item => item?.payer_name || item?.code || item?.payer_id)
    .filter(Boolean)
  return payers.length ? payers.join(', ') : '-'
}

const groupJobsByStep = jobs => {
  const order = [
    'create_batch',
    'audit',
    'auto_fix',
    'acknowledgment',
    'generate_claim',
    'generated_claim_audit',
    'generated_claim_auto_fix',
    'clearance',
    'submit_claim'
  ]
  const map = {}
  jobs.forEach(job => {
    const key = job?.batch_step || job?.job_type || 'other'
    if (!map[key]) map[key] = []
    map[key].push(job)
  })
  return order.filter(key => map[key]).map(key => ({ step: key, jobs: map[key] }))
}

function Detail({ label, children }) {
  return (
    <div>
      <div className='text-[11px] font-semibold uppercase tracking-wide text-gray-500'>
        {label}
      </div>
      <div className='mt-0.5 text-sm font-semibold text-gray-800'>{children}</div>
    </div>
  )
}

function StatTile({ label, value, tone }) {
  const styles = {
    success: 'border-green-200 bg-green-50 text-green-700',
    danger: 'border-red-200 bg-red-50 text-red-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
    neutral: 'border-gray-200 bg-white text-gray-800'
  }
  const { border, bg, valueColor } = styles[tone] || styles.neutral

  return (
    <div className={`rounded border px-3 py-2.5 ${border} ${bg}`}>
      <div className='text-[10.5px] font-semibold uppercase tracking-wide opacity-70'>
        {label}
      </div>
      <div className={`mt-1 text-lg font-bold leading-6 ${valueColor}`}>{value}</div>
    </div>
  )
}

function StepTimeline({ timeline, t }) {
  return (
    <div className='overflow-x-auto rounded border border-gray-200 bg-white'>
      <div className='flex min-w-[1180px] items-start px-4 py-4'>
        {timeline.map((step, index) => {
          const Icon = step.icon || MdCheckCircle
          const done = ['completed', 'completed_with_errors'].includes(step.status)
          const active = step.status === 'processing' || step.status === 'queued'
          const failed = step.status === 'failed'
          const completedAt = step.completed_at || (done ? step.updated_at : null)
          const actorName = step.actor_name
          const isLast = index === timeline.length - 1

          let dotClass = 'border-gray-300 bg-white text-gray-300'
          let lineClass = 'bg-gray-200'
          if (done) {
            dotClass = 'border-green-500 bg-green-500 text-white'
            lineClass = 'bg-green-500'
          } else if (failed) {
            dotClass = 'border-red-400 bg-red-50 text-red-500'
            lineClass = 'bg-red-300'
          } else if (active) {
            dotClass = 'border-amber-400 bg-amber-50 text-amber-500'
            lineClass = 'bg-amber-300'
          }

          return (
            <div key={step.key} className='flex flex-1 flex-col'>
              <div className='flex items-center'>
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 ${dotClass}`}
                >
                  {done ? (
                    <MdCheckCircle className='text-lg' />
                  ) : (
                    <Icon className='text-lg' />
                  )}
                </div>
                {!isLast && <div className={`mx-2 h-0.5 min-w-[36px] flex-1 rounded-full ${lineClass}`} />}
              </div>
              <div className='mt-2 pr-3'>
                <div className='text-sm font-semibold text-gray-700'>{t(step.label)}</div>
                <CustomChip
                  chipType={statusChipType(step.status)}
                  label={normalizeText(step.status) || '-'}
                  className='mt-1 !h-[20px]'
                  labelClassName='!text-[11px]'
                />
                {completedAt && (
                  <div className='mt-1 text-[11px] text-gray-500'>{formatDateTime(completedAt)}</div>
                )}
                {actorName && (
                  <div className='mt-0.5 text-[11px] text-gray-500'>
                    {t('By')}: {actorName}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Extract a display name from an activity_history actor object
const getActorName = actor => {
  if (!actor) return ''
  if (typeof actor === 'string') return actor
  return actor?.full_name || actor?.name || actor?.employee_id || ''
}

function ClaimBatchDetails() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { batchId } = useParams()
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [batch, setBatch] = useState(null)
  const [stoppingJobId, setStoppingJobId] = useState('')
  const [stopConfirmationJob, setStopConfirmationJob] = useState(null)
  const [resendTarget, setResendTarget] = useState(null)
  const [resendingClaimId, setResendingClaimId] = useState('')
  const [generatedIssuesDialog, setGeneratedIssuesDialog] = useState({
    visible: false,
    loading: false,
    issues: []
  })
  const [failureDialog, setFailureDialog] = useState({
    visible: false,
    loading: false,
    job: null,
    failures: []
  })
  const failureStep = failureDialog.job?.batch_step || failureDialog.job?.job_type
  const showResendAction = String(failureStep || '')
    .toLowerCase()
    .includes('submit')

  const fetchBatch = async (showLoading = true) => {
    if (showLoading) setLoading(true)
    else setRefreshing(true)
    try {
      const response = await fetchData('GET', `v3/claims/batches/${batchId}/details`, {})
      setBatch(response?.batch || null)
    } catch (error) {
      console.error('Error fetching batch details', error)
      toast.error(error?.response?.data?.error || t('Failed to load batch details'))
    } finally {
      if (showLoading) setLoading(false)
      else setRefreshing(false)
    }
  }

  useEffect(() => {
    if (batchId) fetchBatch()
  }, [batchId, t])

  const handleStopJob = async () => {
    const job = stopConfirmationJob
    if (!job?.job_id) return

    setStoppingJobId(job.job_id)
    try {
      const response = await fetchData('POST', `v3/claims/batches/${batchId}/jobs/${job.job_id}/stop`, {})
      toast.success(response?.message || t('Batch job stopped successfully'))
      setStopConfirmationJob(null)
      await fetchBatch()
    } catch (error) {
      console.error('Error stopping batch job', error)
      toast.error(error?.response?.data?.error || t('Failed to stop batch job'))
    } finally {
      setStoppingJobId('')
    }
  }

  const handleViewFailures = async job => {
    if (!job?.job_id || !Number(job?.failed_count || 0)) return

    setFailureDialog({ visible: true, loading: true, job, failures: [] })
    try {
      const response = await fetchData('GET', `v3/claims/batches/${batchId}/jobs/${job.job_id}/failures`, {})
      setFailureDialog({ visible: true, loading: false, job, failures: response?.data || [] })
    } catch (error) {
      console.error('Error fetching batch job failures', error)
      toast.error(error?.response?.data?.error || t('Failed to load job failures'))
      setFailureDialog({ visible: false, loading: false, job: null, failures: [] })
    }
  }

  const handleViewGeneratedIssues = async () => {
    setGeneratedIssuesDialog({ visible: true, loading: true, issues: [] })
    try {
      const response = await fetchData('GET', `v3/claims/batches/${batchId}/generated-issues?page=1&limit=200`, {})
      setGeneratedIssuesDialog({ visible: true, loading: false, issues: response?.data || [] })
    } catch (error) {
      console.error('Error fetching generated-claim issues', error)
      toast.error(error?.response?.data?.error || t('Failed to load generated-claim issues'))
      setGeneratedIssuesDialog({ visible: false, loading: false, issues: [] })
    }
  }

  const refreshFailures = async job => {
    if (!job?.job_id) return
    try {
      const response = await fetchData(
        'GET',
        `v3/claims/batches/${batchId}/jobs/${job.job_id}/failures`,
        {}
      )
      setFailureDialog(prev => ({ ...prev, failures: response?.data || [] }))
    } catch (error) {
      console.error('Error refreshing batch job failures', error)
    }
  }

  const handleResendClaim = async () => {
    const claimId = resendTarget?.claim_id
    const job = failureDialog.job
    if (!claimId || resendingClaimId) return

    setResendingClaimId(claimId)
    try {
      try {
        await matchClaimInsurancePlan(claimId)
      } catch (matchError) {
        toast.error(
          matchError?.response?.data?.error ||
            t('Failed to match the claim insurance plan')
        )
        return
      }

      const response = await sendClaim(claimId)
      toast.success(response?.message || t('Claim submitted successfully'))
      await refreshFailures(job)
    } catch (error) {
      if (error?.response) {
        toast.error(error?.response?.data?.error || t('Failed to send claim'))
      } else {
        toast.error(t('Submission status unknown. Refreshing claim status...'))
        await refreshFailures(job)
      }
    } finally {
      setResendingClaimId('')
      setResendTarget(null)
    }
  }

  // Build two lookup maps from activity_history (which carries actor info)
  // 1. job_id  → actor name  (for the Jobs section rows)
  // 2. step    → actor name  (for the Workflow Timeline)
  const { actorByJobId, actorByStep } = useMemo(() => {
    const byJobId = {}
    const byStep = {}
    ;(batch?.activity_history || []).forEach(activity => {
      const name = getActorName(activity?.actor)
      if (!name) return
      if (activity?.job_id) byJobId[activity.job_id] = name
      const step = activity?.step
      if (step && step !== 'manual_step_move') byStep[step] = name
    })
    return { actorByJobId: byJobId, actorByStep: byStep }
  }, [batch])

  const timeline = useMemo(() => {
    const statuses = batch?.step_statuses || {}

    const availableSteps = Number(batch?.workflow_version || 1) >= 2
      ? timelineSteps
      : timelineSteps.filter(step => !['generated_claim_audit', 'generated_claim_auto_fix'].includes(step.key))

    return availableSteps.map(step => {
      const item = statuses?.[step.key] || {}
      const rawStatus =
        item?.status ||
        (batch?.current_step === step.key ? batch?.current_status : 'pending')

      return {
        ...step,
        status: rawStatus === 'locked' ? 'pending' : rawStatus,
        updated_at: item?.updated_at,
        completed_at: item?.completed_at,
        actor_name: item?.actor_name || actorByStep[step.key] || ''
      }
    })
  }, [batch, actorByStep])

  const jobs = useMemo(() => batch?.summary?.job_history || [], [batch])
  const jobGroups = useMemo(() => groupJobsByStep(jobs), [jobs])
  const jobCounts = useMemo(() => batch?.summary?.jobs || {}, [batch])
  const createdBy = batch?.created_by || {}
  const financials = batch?.summary?.financials || {}
  const generatedFinancials = batch?.generated_financials
  const generatedAuditSummary = batch?.generated_audit_summary
  const companies = batch?.companies || []

  const completedSteps = timeline.filter(
    step => step.status === 'completed' || step.status === 'completed_with_errors'
  ).length

  return (
    <div className='main-page-container !overflow-y-auto scrollbar'>
      <PageHeader
        title={`${t('Batch Details')} : ${batch?.batch_number}`}
        iconSize={20}
        Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight}
        onIconClick={() => navigate(-1)}
      >
        <CustomButton
          label={t('Refresh')}
          buttonType='reset'
          icon='pi pi-refresh'
          className='h-[32px]'
          loading={refreshing}
          showLoadingText={false}
          onClick={() => fetchBatch(false)}
          disabled={loading}
        />
      </PageHeader>

      {loading ? (
        <div className='mt-3 space-y-3'>
          <div className='rounded border border-gray-200 bg-gray-50 p-3'>
            <Skeleton width='40%' height='18px' />
            <Skeleton width='70%' height='12px' className='mt-2' />
          </div>
          {companies.length > 0 && (
            <div className='rounded border border-gray-200 bg-white px-4 py-3'>
              <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
                <div>
                  <div className='text-sm font-bold text-gray-800'>{t('TPA Batch')}</div>
                  <div className='text-xs text-gray-500'>
                    {batch?.tpa_name || t('TPA not configured')} - {batch?.batch_number}
                  </div>
                </div>
                <span className='text-xs text-gray-500'>
                  {companies.length} {t('Insurance Companies')}
                </span>
              </div>
              <div className='overflow-x-auto'>
                <table className='min-w-[900px] w-full text-left text-xs'>
                  <thead className='border-y border-gray-200 bg-gray-50 text-gray-600'>
                    <tr>
                      <th className='px-2 py-2 font-semibold'>{t('Company Batch')}</th>
                      <th className='px-2 py-2 font-semibold'>{t('Insurance Company')}</th>
                      <th className='px-2 py-2 font-semibold'>{t('Claims')}</th>
                      <th className='px-2 py-2 font-semibold'>{t('Quantity')}</th>
                      <th className='px-2 py-2 font-semibold'>{t('Gross')}</th>
                      <th className='px-2 py-2 font-semibold'>{t('Discount')}</th>
                      <th className='px-2 py-2 font-semibold'>{t('Patient Share VAT')}</th>
                      <th className='px-2 py-2 font-semibold'>{t('Payer Share VAT')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.map(company => {
                      const totals = company?.initialization_financials || {}
                      return (
                        <tr key={company?.batch_number} className='border-b border-gray-100 last:border-0'>
                          <td className='px-2 py-2 font-semibold text-gray-800'>{company?.batch_number || '-'}</td>
                          <td className='px-2 py-2'>{company?.payer?.payer_name || company?.payer?.code || '-'}</td>
                          <td className='px-2 py-2'>{totals?.claim_count ?? company?.visit_count ?? 0}</td>
                          <td className='px-2 py-2'>{totals?.quantity || 0}</td>
                          <td className='px-2 py-2'>{formatAmount(totals?.gross_amount)}</td>
                          <td className='px-2 py-2'>{formatAmount(totals?.discount)}</td>
                          <td className='px-2 py-2'>{formatAmount(totals?.patient_share_with_vat)}</td>
                          <td className='px-2 py-2'>{formatAmount(totals?.payer_share_with_vat)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className='grid grid-cols-2 gap-2 lg:grid-cols-5'>
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} height='60px' />
            ))}
          </div>
          <div className='rounded border border-gray-200 bg-white'>
            {[1, 2, 3, 4, 5, 6, 7].map(i => (
              <Skeleton key={i} width='100%' height='44px' />
            ))}
          </div>
        </div>
      ) : !batch ? (
        <div className='mt-3 rounded border border-gray-200 bg-white py-12 text-center text-sm text-gray-500'>
          {t('Batch details not found')}
        </div>
      ) : (
        <div className='mt-3 space-y-4'>
          {/* {   <div className='flex flex-wrap items-center justify-between gap-2'>
            <h2 className='text-xl font-bold text-gray-900'>
              {batch?.batch_number || '-'}
            </h2>
            <div className='text-xs text-gray-500'>
              {t('Updated')}: {formatDateTime(batch?.updated_at)}
            </div>
          </div>} */}

          <div className='grid grid-cols-2 gap-x-6 gap-y-3 rounded border border-gray-200 bg-gray-50 px-4 py-3.5 sm:grid-cols-3 xl:grid-cols-6'>
            <Detail label={t('Status')}>
              <CustomChip
                chipType={statusChipType(batch?.current_status)}
                label={normalizeText(batch?.current_status) || '-'}
                className='!h-[22px]'
                labelClassName='!text-[11px]'
              />
            </Detail>
            <Detail label={t('Current Step')}>
              {normalizeText(batch?.current_step) || '-'}
            </Detail>
            <Detail label={t('Payer')}>{getPayerDisplay(batch)}</Detail>
            <Detail label={t('Date Range')}>
              {formatDate(batch?.start_date)} - {formatDate(batch?.end_date)}
            </Detail>
            <Detail label={t('Last Completed')}>
              {normalizeText(batch?.last_completed_step) || '-'}
            </Detail>
            <Detail label={t('Created By')}>
              {createdBy?.full_name || createdBy?.employee_id || '-'}
              {createdBy?.role && (
                <span className='block text-xs font-normal text-gray-500'>
                  {createdBy.role} · {formatDateTime(batch?.created_at)}
                </span>
              )}
            </Detail>
            <Detail label={t('Visits')}>{batch?.visit_ids?.length || 0}</Detail>
            <Detail label={t('Paid Items')}>{financials?.paid_item_count || 0}</Detail>
            <Detail label={t('Invoices')}>{financials?.invoice_count || 0}</Detail>
          </div>

          <div className='grid grid-cols-2 gap-2 lg:grid-cols-5'>
            <StatTile label={t('Patient Share')} value={formatAmount(financials?.patient_share)} tone='blue' />
            <StatTile label={t('Company Share')} value={formatAmount(financials?.company_share)} tone='neutral' />
            <StatTile label={t('Discount')} value={formatAmount(financials?.discount)} tone='amber' />
            <StatTile label={t('VAT')} value={formatAmount(financials?.total_vat)} tone='amber' />
            <StatTile
              label={t('Net Total')}
              value={formatAmount(Number(financials?.net_company || 0) + Number(financials?.net_patient || 0))}
              tone='success'
            />
          </div>

          {generatedFinancials?.calculated_at && (
            <div className='rounded border border-blue-200 bg-blue-50/50 px-4 py-3'>
              <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
                <div className='text-sm font-semibold text-gray-800'>
                  {t('Generated Claim Financial Totals')}
                </div>
                <div className='text-xs text-gray-600'>
                  {generatedFinancials?.claim_count || 0} {t('Claims')} ·{' '}
                  {formatDateTime(generatedFinancials?.calculated_at)}
                </div>
              </div>
              <div className='grid grid-cols-2 gap-2 lg:grid-cols-4'>
                <StatTile label={t('Patient Share')} value={formatAmount(generatedFinancials?.patient_share)} tone='blue' />
                <StatTile label={t('Company Share')} value={formatAmount(generatedFinancials?.company_share)} tone='neutral' />
                <StatTile label={t('Discount')} value={formatAmount(generatedFinancials?.discount)} tone='amber' />
                <StatTile label={t('VAT')} value={formatAmount(generatedFinancials?.vat)} tone='amber' />
              </div>
            </div>
          )}

          {generatedAuditSummary?.calculated_at && (
            <div className='rounded border border-amber-200 bg-amber-50/50 px-4 py-3'>
              <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
                <div>
                  <div className='text-sm font-semibold text-gray-800'>{t('Generated Claim Audit')}</div>
                  <div className='text-xs text-gray-600'>{formatDateTime(generatedAuditSummary.calculated_at)}</div>
                </div>
                <CustomButton
                  label={t('View Issues')}
                  buttonType='warning'
                  className='h-[30px]'
                  onClick={handleViewGeneratedIssues}
                />
              </div>
              <div className='grid grid-cols-2 gap-2 lg:grid-cols-5'>
                <StatTile label={t('Claims')} value={generatedAuditSummary?.total_claims || 0} tone='blue' />
                <StatTile label={t('Taxable Items')} value={generatedAuditSummary?.taxable_items || 0} tone='neutral' />
                <StatTile label={t('Issues')} value={generatedAuditSummary?.total_issues || 0} tone='amber' />
                <StatTile label={t('Resolved')} value={generatedAuditSummary?.resolved_issues || 0} tone='success' />
                <StatTile label={t('Unresolved')} value={generatedAuditSummary?.unresolved_issues || 0} tone={generatedAuditSummary?.unresolved_issues ? 'danger' : 'success'} />
              </div>
              {!!Object.keys(generatedAuditSummary?.issue_counts || {}).length && (
                <div className='mt-2 flex flex-wrap gap-1.5'>
                  {Object.entries(generatedAuditSummary.issue_counts).map(([issueType, count]) => (
                    <span key={issueType} className='rounded border border-amber-200 bg-white px-2 py-1 text-xs text-amber-800'>
                      {normalizeText(issueType)}: {count}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className='grid grid-cols-2 gap-2 lg:grid-cols-5'>
            <StatTile label={t('Jobs Run')} value={jobCounts?.total || 0} tone='blue' />
            <StatTile label={t('Queued')} value={jobCounts?.queued || 0} tone='neutral' />
            <StatTile label={t('Running')} value={jobCounts?.processing || 0} tone='amber' />
            <StatTile label={t('Completed')} value={jobCounts?.completed || 0} tone='success' />
            <StatTile label={t('Failed')} value={jobCounts?.failed || 0} tone='danger' />
          </div>

          <div>
            <div className='mb-2 flex items-center justify-between'>
              <h3 className='text-base font-bold text-gray-800'>{t('Workflow Steps')}</h3>
              <span className='text-xs text-gray-500'>
                {completedSteps}/{timeline.length} {t('completed')}
              </span>
            </div>
            <StepTimeline timeline={timeline} t={t} />
          </div>

          {/* job history grouped by step */}
          <div>
            <div className='mb-2 flex items-center justify-between'>
              <h3 className='text-base font-bold text-gray-800'>{t('Jobs')}</h3>
              <span className='text-xs text-gray-500'>
                {jobs.length} {t('Runs')}
              </span>
            </div>
            {!jobGroups.length ? (
              <div className='rounded border border-gray-200 bg-white px-3 py-6 text-center text-sm text-gray-500'>
                {t('No jobs found for this batch')}
              </div>
            ) : (
              <div className='space-y-3'>
                {jobGroups.map(group => (
                  <div key={group.step} className='rounded border border-gray-200 bg-white'>
                    <div className='flex items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2'>
                      <div className='text-sm font-semibold text-gray-700'>
                        {normalizeText(group.step) || '-'}
                      </div>
                      <span className='text-xs text-gray-500'>
                        {group.jobs.length} {t('Runs')}
                      </span>
                    </div>
                    <div className='divide-y divide-gray-100'>
                      {group.jobs.map(job => {
                        const isStoppable = ['queued', 'processing'].includes(
                          String(job?.status || '').toLowerCase()
                        )
                        const progress = Math.min(100, Number(job?.progress_percentage || 0))
                        const donePct = getJobResultPercent(job, 'success')
                        const failedPct = getJobResultPercent(job, 'failed')

                        return (
                          <div key={job?.job_id} className='px-3 py-2.5'>
                            <div className='flex flex-wrap items-center gap-x-4 gap-y-1.5'>
                              <div className='font-mono text-xs text-gray-500'>
                                {t('Job ID')}: {job?.job_id || '-'}
                              </div>
                              <div className='text-xs text-gray-600'>
                                {t('Started')}: {formatDateTime(job?.started_at || job?.created_at)}
                              </div>
                              {job?.completed_at && (
                                <div className='text-xs text-gray-600'>
                                  {t('Completed')}: {formatDateTime(job?.completed_at)}
                                </div>
                              )}
                              {actorByJobId[job?.job_id] && (
                                <div className='text-xs text-gray-600'>
                                  {t('Run By')}: {actorByJobId[job?.job_id]}
                                </div>
                              )}
                              {isStoppable && (
                                <CustomButton
                                  label={t('Stop')}
                                  buttonType='danger'
                                  className='!h-[24px] !text-[11px]'
                                  loading={stoppingJobId === job?.job_id}
                                  showLoadingText={false}
                                  onClick={() => setStopConfirmationJob(job)}
                                />
                              )}
                              <CustomChip
                                chipType={statusChipType(job?.status)}
                                label={normalizeText(job?.status) || '-'}
                                className='ms-auto !h-[20px]'
                                labelClassName='!text-[11px]'
                              />
                            </div>

                            <div className='mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-gray-600'>
                              <span>{t('Processed')}: {job?.processed_count || 0}</span>
                              <span className='text-green-700'>{t('Success')}: {job?.success_count || 0}</span>
                              <span className='text-amber-600'>{t('Skipped')}: {job?.skipped_count || 0}</span>
                              {Number(job?.failed_count || 0) > 0 ? (
                                <button
                                  type='button'
                                  className='font-semibold text-red-600 underline underline-offset-2'
                                  onClick={() => handleViewFailures(job)}
                                >
                                  {t('Failed')}: {job?.failed_count || 0}
                                </button>
                              ) : (
                                <span>{t('Failed')}: 0</span>
                              )}
                              <span className='text-gray-400'>
                                {progress.toFixed(1)}%
                              </span>
                            </div>

                            <div className='mt-1.5 flex h-1.5 w-full overflow-hidden rounded-full bg-gray-100'>
                              {failedPct > 0 ? (
                                <div className='flex h-full' style={{ width: `${progress}%` }}>
                                  <div
                                    className='h-full bg-green-600'
                                    style={{ width: `${(donePct / Math.max(progress, 1)) * 100}%` }}
                                  />
                                  <div
                                    className='h-full bg-red-500'
                                    style={{ width: `${(failedPct / Math.max(progress, 1)) * 100}%` }}
                                  />
                                </div>
                              ) : (
                                <div
                                  className='h-full bg-success'
                                  style={{ width: `${progress}%` }}
                                />
                              )}
                            </div>

                            {!!job?.error_message && job?.error_message !== '<nil>' && (
                              <div className='mt-1.5 rounded bg-red-50 px-2 py-1 text-[11px] text-red-600'>
                                {job.error_message}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmationModal
        visible={!!stopConfirmationJob}
        setVisible={visible => {
          if (!visible) setStopConfirmationJob(null)
        }}
        onAccept={handleStopJob}
        onReject={() => setStopConfirmationJob(null)}
        header={t('Stop Job')}
        message={t('Are you sure you want to stop this batch job?')}
        acceptLabel={t('Stop')}
        rejectLabel={t('Cancel')}
        position='top'
        isLoading={stoppingJobId === stopConfirmationJob?.job_id}
      />

      <ReusableModal
        isVisible={generatedIssuesDialog.visible}
        onClose={() => setGeneratedIssuesDialog({ visible: false, loading: false, issues: [] })}
        title={t('Generated Claim Issues')}
        HeaderIcon={MdFactCheck}
        position='top'
        className='w-[96vw] md:w-[1100px]'
      >
        {generatedIssuesDialog.loading ? (
          <div className='py-8 text-center text-sm text-gray-500'>{t('Loading')}...</div>
        ) : !generatedIssuesDialog.issues.length ? (
          <div className='py-8 text-center text-sm text-gray-500'>{t('No issues found')}</div>
        ) : (
          <div className='max-h-[65vh] overflow-auto rounded border border-gray-200'>
            <table className='w-full min-w-[980px] text-left text-xs'>
              <thead className='sticky top-0 bg-gray-50 text-gray-700'>
                <tr>
                  <th className='px-3 py-2'>{t('Claim ID')}</th>
                  <th className='px-3 py-2'>{t('Visit ID')}</th>
                  <th className='px-3 py-2'>{t('Item ID')}</th>
                  <th className='px-3 py-2'>{t('Issue Type')}</th>
                  <th className='px-3 py-2'>{t('Actual Value')}</th>
                  <th className='px-3 py-2'>{t('Expected Value')}</th>
                  <th className='px-3 py-2'>{t('Corrected Value')}</th>
                  <th className='px-3 py-2'>{t('Status')}</th>
                  <th className='px-3 py-2'>{t('Failure Reason')}</th>
                </tr>
              </thead>
              <tbody>
                {generatedIssuesDialog.issues.map(issue => (
                  <tr key={issue?.id} className='border-t border-gray-100 align-top'>
                    <td className='px-3 py-2'>{issue?.claim_id || '-'}</td>
                    <td className='px-3 py-2'>{issue?.visit_id || '-'}</td>
                    <td className='px-3 py-2'>{issue?.item_id || '-'}</td>
                    <td className='px-3 py-2'>{normalizeText(issue?.issue_type) || '-'}</td>
                    <td className='px-3 py-2'>{issue?.actual_value ?? '-'}</td>
                    <td className='px-3 py-2'>{issue?.expected_value ?? '-'}</td>
                    <td className='px-3 py-2'>{issue?.corrected_value ?? '-'}</td>
                    <td className='px-3 py-2'><CustomChip chipType={statusChipType(issue?.status)} label={normalizeText(issue?.status) || '-'} className='!h-[20px]' labelClassName='!text-[11px]' /></td>
                    <td className='max-w-[260px] break-words px-3 py-2 text-red-600'>{issue?.failure_reason || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ReusableModal>

      <ReusableModal
        isVisible={failureDialog.visible}
        onClose={() => setFailureDialog({ visible: false, loading: false, job: null, failures: [] })}
        title={`${t('Failed Records')} - ${normalizeText(failureDialog.job?.batch_step || failureDialog.job?.job_type)}`}
        position='top'
        className='w-[95vw] md:w-[900px]'
      >
        {failureDialog.loading ? (
          <div className='py-8 text-center text-sm text-gray-500'>{t('Loading')}...</div>
        ) : !failureDialog.failures.length ? (
          <div className='py-8 text-center text-sm text-gray-500'>{t('No failure details found')}</div>
        ) : (
          <div className='max-h-[60vh] overflow-auto rounded border border-gray-200'>
            <table className='w-full min-w-[760px] text-left text-xs'>
              <thead className='sticky top-0 bg-gray-50 text-gray-700'>
                <tr>
                  {showResendAction && (
                    <th className='px-3 py-2'>{t('Actions')}</th>
                  )}
                  <th className='px-3 py-2'>{t('Visit ID')}</th>
                  <th className='px-3 py-2'>{t('Patient Name')}</th>
                  <th className='px-3 py-2'>{t('MRN')}</th>
                  <th className='px-3 py-2'>{t('Error')}</th>
                  <th className='px-3 py-2'>{t('Failed At')}</th>
                </tr>
              </thead>
              <tbody>
                {failureDialog.failures.map((failure, index) => (
                  <tr
                    key={`${failure?.visit_id || failure?.claim_id || 'failure'}-${index}`}
                    className='border-t border-gray-100 align-top'
                  >
                    {showResendAction && (
                      <td className='px-3 py-2'>
                        {failure?.claim_id ? (
                          <IoIosSend
                            size={16}
                            title={t('Send Again')}
                            className={
                              resendingClaimId
                                ? 'cursor-not-allowed text-gray-400'
                                : 'cursor-pointer text-primary'
                            }
                            onClick={() =>
                              !resendingClaimId && setResendTarget(failure)
                            }
                          />
                        ) : (
                          '-'
                        )}
                      </td>
                    )}
                    <td className='px-3 py-2'>{failure?.visit_id || '-'}</td>
                    <td className='px-3 py-2'>{failure?.patient_name || '-'}</td>
                    <td className='px-3 py-2'>{failure?.file_number || '-'}</td>
                    <td className='max-w-[340px] break-words px-3 py-2 text-red-600'>
                      {formatFailureError(failure?.error_message)}
                    </td>
                    <td className='px-3 py-2'>{formatDateTime(failure?.failed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ReusableModal>

      <ConfirmationModal
        visible={!!resendTarget}
        setVisible={visible => {
          if (!visible) setResendTarget(null)
        }}
        onAccept={handleResendClaim}
        onReject={() => setResendTarget(null)}
        header={t('Send Again')}
        message={t(
          'Are you sure you want to send this claim again? A new submission attempt will be recorded.'
        )}
        acceptLabel={t('Send Again')}
        rejectLabel={t('Cancel')}
        position='top'
        isLoading={!!resendingClaimId}
      />
    </div>
  )
}

export default ClaimBatchDetails
